import { AccountVerification, Modal, UI, Utils } from 'common';
import Typography from 'common/src/code/ui/Typography.component';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import GetUsernameModal from './GetUsernameModal.component';

const { ModalButtonActionType } = Modal;
const {
	Footer,
	Link,
	SaveConfirmation,
	Section,
	SectionBody,
	WrappedInputTextWithLabel,
} = UI;
const { Logger, Session, Util, Validation, withLayoutAndModalContexts } = Utils;
const { AccountVerificationModal } = AccountVerification;

// this is the max money value that DB allows (truncating the decimal portion)
const MAXIMUM_LIMIT_AMOUNT = 922337203685477;

class PersonalContactInfo extends Component {
	constructor(props) {
		super(props);

		Session.setFormStateDirty(false);

		this.state = {
			firstName: '',
			lastName: '',
			email: '',
			phoneNumber: '',
			initialState: {},
			savedUserData: {},
			validations: {
				firstName: 0,
				lastName: 0,
				email: 0,
				phoneNumber: 0,
			},
			errorMessages: {
				firstName: '',
				lastName: '',
				email: '',
				phoneNumber: '',
			},
			clean: true,
			saved: false,
		};
	}

	async componentDidMount() {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await AccountCommunication.getUserContactInfo();

		if (!AccountCommunication.isSuccessful(response)) {
			return showError({ response });
		}

		const { UserInfo } = response.data;
		const { FirstName, LastName, EmailAddress, PhoneNumber } = UserInfo;
		const initialState = {
			firstName: FirstName,
			lastName: LastName,
			email: EmailAddress,
			phoneNumber: Util.formatPhoneNumber(PhoneNumber),
		};

		this.setState({
			...initialState,
			initialState,
			savedUserData: UserInfo,
		});
	}

	onFormSubmit = e => {
		e.preventDefault();

		const {
			modalContext: { showModal },
		} = this.props;
		const { email, phoneNumber, initialState } = this.state;

		// validate all fields and prevent form submission if any fields are invalid
		if (!this.triggerValidation(true)) {
			return;
		}

		const { isEmailChanged, isPhoneNumberChanged } =
			Util.checkContactChangesRequireVerification({
				oldPhoneNumber: initialState.phoneNumber,
				newPhoneNumber: phoneNumber,
				oldEmailAddress: initialState.email,
				newEmailAddress: email,
			});

		if (isEmailChanged || isPhoneNumberChanged) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.closeAccountVerificationCallback,
					isChangingEmailAddress: isEmailChanged,
				},
			});

			return;
		}

		Logger.saveActionData('Save Personal Contact Info Button Clicked');

		this.updateUserInfo();
	};

	closeAccountVerificationCallback = success => {
		const {
			modalContext: { showError },
		} = this.props;

		if (success) {
			this.updateUserInfo();
		} else {
			setTimeout(() => {
				showError({
					data: {
						title: 'Verification Required',
						description:
							'To change your phone number or email, you must verify your identity.',
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Close',
						handleType: 'custom',
					},
				});
			}, 0);
		}
	};

	updateUserInfo = async () => {
		const {
			firstName,
			lastName,
			email,
			phoneNumber,
			savedUserData,
			initialState,
		} = this.state;
		const {
			modalContext: { showError },
		} = this.props;
		const fieldsChanged = this.getChanges();

		const { accountInfo, settings } = Session.getData();
		const { username = '' } = isObject(accountInfo.customerDetails)
			? accountInfo.customerDetails
			: {};
		const { MerchantType } = settings;
		const userCapabilities = savedUserData.UserCapabilities;

		// check if per-print limit exceeds maximum value allowed
		const exceedPerPrintLimitMaximum =
			isObject(userCapabilities) &&
			isNumber(userCapabilities.PerPrintMaximumAmountLimit) &&
			userCapabilities.PerPrintMaximumAmountLimit > MAXIMUM_LIMIT_AMOUNT;

		const newInitialState = {
			firstName,
			lastName,
			email,
			phoneNumber,
		};

		const response = await AccountCommunication.updateUser({
			User: {
				...savedUserData,
				FirstName: firstName,
				LastName: lastName,
				EmailAddress: email,
				PhoneNumber: Util.removePhoneFormatting(phoneNumber),

				// per WEBAPPS-24338, per-print limit should not exceed 922337203685477
				UserCapabilities: exceedPerPrintLimitMaximum
					? {
							...userCapabilities,
							PerPrintMaximumAmountLimit: MAXIMUM_LIMIT_AMOUNT,
						}
					: userCapabilities,
			},
		});

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				initialState: newInitialState,
				clean: true,
				saved: true,
			});

			if (
				fieldsChanged.includes('email') ||
				fieldsChanged.includes('phoneNumber')
			) {
				const emailResponse = await AccountCommunication.sendEmail({
					TemplateName: 'personalContactInfoChanged',
					NvPairs: {
						merchant: MerchantType,
						merchantUrl: 'stamps.com',
						strEmailAddress: initialState.email,
						strFirstName: firstName,
						strUserName: username,
						brandName: 'Stamps.com',
						subject: 'Stamps.com Contact Info Change Confirmation',
						infoChanged: this.infoChanged(fieldsChanged),
						fraudNumber: Util.getFraudNumber(),
					},
				});

				if (!AccountCommunication.isSuccessful(emailResponse)) {
					showError({ emailResponse });
				}
			}

			Session.setFormStateDirty(false);

			Logger.logToServer({
				category: 'Save Changes',
				fieldsChanged,
			});
		} else {
			showError({ response });
		}
	};

	// returns a string with the required information for the SendGrid email template
	infoChanged = fieldsChanged => {
		const newFields = fieldsChanged.reduce((acc, curr) => {
			if (curr === 'email') {
				acc.push('email address');
			} else if (curr === 'phoneNumber') {
				acc.push('phone number');
			}
			return acc;
		}, []);

		return newFields.join(' and ');
	};

	onFieldChange = ({ target: { value, name } }) => {
		this.setState({
			[name]: name === 'phoneNumber' ? Util.formatPhoneNumber(value) : value,
			clean: false,
			saved: false,
		});

		Session.setFormStateDirty(true);
	};

	onFieldBlur = ({ target: { name } }) => {
		this.triggerValidation(false, name);
	};

	triggerValidation = (onSubmit = false, onBlurField) => {
		const { validations, errorMessages } = this.state;
		const {
			modalContext: { showError },
		} = this.props;
		const newValidations = { ...validations };
		const newErrorMessages = { ...errorMessages };
		let isValid = true;

		Object.keys(validations).forEach(name => {
			const fieldValue = this.state[name];

			// validate all fields for onSubmit event, only validate focused field for onBlur event
			if (onSubmit || (onSubmit === false && name === onBlurField)) {
				if (fieldValue === '') {
					newValidations[name] = -1;
					newErrorMessages[name] = `${Util.formatFieldName(name)} is required.`;
					isValid = false;
				} else if (name === 'email' && !Validation.isValidEmail(fieldValue)) {
					newValidations[name] = -1;
					newErrorMessages[name] = 'Invalid email address.';
					isValid = false;
				} else if (
					name === 'phoneNumber' &&
					Util.getPhoneNumber(fieldValue).length < 10
				) {
					newValidations[name] = -1;
					newErrorMessages[name] = 'Invalid phone number.';
					isValid = false;
				} else {
					newValidations[name] = 1;
					newErrorMessages[name] = '';
				}
			}
		});

		this.setState({
			validations: newValidations,
			errorMessages: newErrorMessages,
		});

		if (!isValid && onSubmit) {
			showError({
				data: {
					title: 'Incomplete Fields',
					description:
						Validation.buildValidationErrorDescription(newErrorMessages),
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});

			return false;
		}

		return true;
	};

	getChanges = () => {
		const { initialState } = this.state;

		const fieldsChanged = Object.keys(initialState).filter(
			key => initialState[key] !== this.state[key]
		);

		return fieldsChanged;
	};

	openGetUsernameModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;
		const { accountInfo } = Session.getData();
		const { username = '' } = isObject(accountInfo.customerDetails)
			? accountInfo.customerDetails
			: {};

		showModal({
			component: GetUsernameModal,
			data: {
				username,
			},
		});
	};

	render() {
		const {
			firstName,
			lastName,
			email,
			phoneNumber,
			validations,
			errorMessages,
			clean,
			saved,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		const mobileCls = isMobile ? ' mobile-wrapper' : '';
		const primaryButton = {
			label: 'Save',
			onClick: this.onFormSubmit,
			disabled: clean,
			dataqa: 'contact-info-save-button',
		};

		return (
			<div className="account-personal-contact-info">
				<Section>
					<div className="account-title">
						Personal Contact Info
						{saved && <SaveConfirmation success text="Changes Saved" />}
					</div>

					<form onSubmit={this.onFormSubmit}>
						<SectionBody>
							<WrappedInputTextWithLabel
								isInputInvalid={validations.firstName === -1}
								showErrorBelow={isMobile}
								errorMessage={errorMessages.firstName}
								className={mobileCls}
								label="First Name:"
								uiElementConfig={{
									maxLength: 40,
									onChange: this.onFieldChange,
									onBlur: this.onFieldBlur,
									value: firstName,
									dataqa: 'firstname',
									name: 'firstName',
								}}
							/>
							<WrappedInputTextWithLabel
								isInputInvalid={validations.lastName === -1}
								showErrorBelow={isMobile}
								errorMessage={errorMessages.lastName}
								className={mobileCls}
								label="Last Name:"
								uiElementConfig={{
									maxLength: 40,
									onChange: this.onFieldChange,
									onBlur: this.onFieldBlur,
									value: lastName,
									dataqa: 'lastname',
									name: 'lastName',
								}}
							/>
							<WrappedInputTextWithLabel
								isInputInvalid={validations.email === -1}
								showErrorBelow={isMobile}
								errorMessage={errorMessages.email}
								className={mobileCls}
								label="Email:"
								uiElementConfig={{
									maxLength: 255,
									onChange: this.onFieldChange,
									onBlur: this.onFieldBlur,
									value: email,
									dataqa: 'email',
									name: 'email',
								}}
							/>
							<WrappedInputTextWithLabel
								isInputInvalid={validations.phoneNumber === -1}
								showErrorBelow={isMobile}
								errorMessage={errorMessages.phoneNumber}
								className={`phone${mobileCls}`}
								label="Phone:"
								uiElementConfig={{
									maxLength: 23,
									onChange: this.onFieldChange,
									onBlur: this.onFieldBlur,
									value: phoneNumber,
									dataqa: 'phonenumber',
									name: 'phoneNumber',
								}}
							/>
							<div className="get-username-link">
								<Typography.P>
									Need a username to access a partner solution?
								</Typography.P>
								<Link
									onClick={e => {
										e.preventDefault();
										this.openGetUsernameModal();
									}}
									dataqa="get-username-link"
								>
									Get username
								</Link>
							</div>
						</SectionBody>
						<Footer
							primary={primaryButton}
							className={`account-footer${mobileCls}`}
						/>
					</form>
				</Section>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(PersonalContactInfo);
